import { memo } from 'react'
import { ButtonBase, ClickAwayListener, Modal, SxProps } from '@mui/material'
import { SearchIcon } from '@/assets'
import { useTranslations } from '@/core/translation'
import { useDeviceType } from '@/utils'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { Input, InputBase } from '@/components/Inputs'
import Popper from '../../Popper'
import { bannerSearchStyles as styles } from './BannerSearch.style'
import {
  BannerSearchDropdownListProps,
  BannerSearchMobileModalProps,
  BannerSearchProps,
} from './BannerSearch.types'
import { useBannerSearchController } from './BannerSearch.controller'
import BannerDropdownList from './BannerDropdownList'

function BannerSearch({ searchInputProps, noFullscreenSearch }: BannerSearchProps) {
  const t = useTranslations('banner')

  const { isMobile } = useDeviceType()

  const {
    keyword,
    searchList,
    autoCompleteOpen,
    setAutoCompleteOpen,
    mobileModalOpen,
    onWrapperClick,
    onInputFocus,
    onInputChange,
    onKeywordChange,
    onMobileModalClose,
    wrapperRef,
  } = useBannerSearchController({
    isMobile,
    hasExternalOnChange: !!searchInputProps?.onChange,
    noFullscreenSearch,
  })

  const hasData = Object.values(searchList).some((list) => list.length > 0)

  return (
    <>
      <ClickAwayListener onClickAway={() => setAutoCompleteOpen(false)}>
        <Box
          sx={
            [
              styles.searchPlaceHolder,
              !isMobile && autoCompleteOpen && styles.searchPlaceHolderActive,
            ] as SxProps
          }
          ref={wrapperRef}
          onClickCapture={onWrapperClick}>
          <Box sx={styles.searchIcon}>
            <SearchIcon />
          </Box>

          <InputBase
            sx={styles.searchInput}
            placeholder={t('search.placeholder.global')}
            value={keyword}
            inputProps={{
              readOnly: isMobile && !noFullscreenSearch,
            }}
            onFocus={onInputFocus}
            onChange={onInputChange}
            {...searchInputProps}
          />

          {!isMobile && (
            <Popper
              open={autoCompleteOpen}
              anchorEl={wrapperRef.current}
              parentWidth
              sx={styles.dropdownContentWrapper}>
              <Box>
                <BannerSearchDropdownList searchList={searchList} hasData={hasData} />
              </Box>
            </Popper>
          )}
        </Box>
      </ClickAwayListener>

      {isMobile && (
        <BannerSearchMobileModal
          open={mobileModalOpen}
          onClose={onMobileModalClose}
          keyword={keyword}
          onKeywordChange={onKeywordChange}>
          <BannerSearchDropdownList searchList={searchList} hasData={hasData} />
        </BannerSearchMobileModal>
      )}
    </>
  )
}

export default memo(BannerSearch)

function BannerSearchMobileModal({
  open,
  onClose,
  keyword,
  onKeywordChange,
  children,
}: BannerSearchMobileModalProps) {
  const t = useTranslations('banner')
  return (
    <Modal open={open} onClose={onClose} hideBackdrop>
      <Box sx={styles.mobileModal.root}>
        {/* Header: search row + close button */}
        <Box sx={styles.mobileModal.header}>
          <Input
            value={keyword}
            onChange={onKeywordChange}
            placeholder={t('search.placeholder.global')}
            startAdornment={<SearchIcon />}
            autoFocus
          />

          <ButtonBase sx={styles.mobileModal.closeBtn} onClick={onClose}>
            <Text sx={styles.mobileModal.closeBtnText}>{t('search.close')}</Text>
          </ButtonBase>
        </Box>

        {/* Scrollable results */}
        <Box sx={styles.mobileModal.body}>{children}</Box>
      </Box>
    </Modal>
  )
}

function BannerSearchDropdownList({ searchList, hasData }: BannerSearchDropdownListProps) {
  const t = useTranslations('banner')

  return (
    <>
      <BannerDropdownList title={t('search.trending')} data={searchList.trending} />
      <BannerDropdownList title={t('search.performers')} data={searchList.performers} />
      <BannerDropdownList title={t('search.events')} data={searchList.events} />
      <BannerDropdownList title={t('search.venues')} data={searchList.venues} />
      {!hasData && <Text sx={styles.noDataText}>{t('search.noData')}</Text>}
    </>
  )
}
